import './styles.scss'

import { Box, ClickAwayListener, Typography } from '@mui/material'
import { withObservables } from '@nozbe/watermelondb/react'
import { InviteLinkRow } from 'components/badges/InviteLinkBadge'
import { CalendarHeaderTitle } from 'components/calendar/styled'
import ContainerWithBottomBadgeIcon from 'components/containers/ContainerWithBottomBadgeIcon'
import ConfirmationDialog from 'components/dialogs/ConfirmationDialog'
import { useConnection } from 'components/providers/ConnectionProvider'
import ProgressProvider, {
  useProgress
} from 'components/providers/ProgressProvider'
import TierHover from 'components/tier/TierHover'
import i18n from 'i18n'
import { t } from 'i18next'
import { GroupPrivacy, GroupVerified } from 'model/models/Group'
import { ComponentType, ReactNode, useEffect, useState } from 'react'
import compose from 'recompose/compose'
import { theme } from 'theme'
import { highestActiveTier, Tier } from 'utils/api/accounts'
import {
  defaultGroupImage,
  featureToMinimumTierNeeded,
  getActiveParticipants,
  isAddingMemberAllowedForGroup,
  isGroupChannel,
  isGroupOutOfTier,
  isUserActiveInGroup,
  isUserAdminInGroup,
  isUserOwnerOfGroup
} from 'utils/api/groups'
import { APP_COLOR } from 'utils/colors'

import { CloseIcon, GroupChannelIcon, PlusIcon } from '../../../assets/icons'
import { CircleImagePicker } from '../../../components/calendar/CircleImagePicker'
import LoadingContainer from '../../../components/containers/LoadingContainer'
import MembersList from '../../../components/lists/MembersList'
import Popup, { PopupInstance } from '../../../components/popup/Popup'
import PopupSection from '../../../components/popup/PopupSection'
import {
  generateLinkForGroup,
  resetConvertedGroupState,
  updateGroupColor,
  updateGroupPersonalSyncStatus,
  updateGroupPhoto,
  updateGroupTitle,
  updateLinkOnServer
} from '../../../services/api/group'
import ObserveService from '../../../services/db/Observe'
import {
  isOwnerForGroup,
  isUserLeft
} from '../../../services/db/utils/GroupUtils'
import LocalStorage from '../../../services/LocalStorage'
import Logger from '../../../services/Logger'
import {
  AccountDoc,
  GroupDoc,
  GroupParticipant,
  GroupParticipantStatus,
  GroupStatus,
  UserSettingsDoc
} from '../../../types/api/changes'
import { Model } from '../../../types/model'
import { LocalStorageKey } from '../../../types/services/localStorage'
import { parseErrorToMessage } from '../eventInfo/EventPopup'
import * as Styled from '../styled'
import BusinessInfoSection from './BusinessInfoSection'
import ChannelToggle from './ChannelToggle'
import { ExitGroupButton } from './ExitGroupButton'
import GroupTitle from './GroupTitle'
import InputWithAction from './InputWithAction'
import MakeGroupVerified from './MakeGroupVerified'
import MemberCanSection from './MembersCanSection'
import RemoveLinkRow from './RemoveLinkRow'
import SettingsForCalendarSection, {
  performUpdateGroupSettings
} from './SettingsForCalendarSection'
import { TextButtonContainer } from './styled'
import EmbedCalendarDialog from 'components/dialogs/embed/EmbedCalendarDialog'
import { PopupSectionRow } from 'components/popup/PopupSectionRow'
import CalendarProvider, {
  useCalendar
} from 'components/providers/CalendarProvider'
import { isGroupDefault } from 'utils/api/userSettings'
import { groupToProvider, isConvertedGroup, ProviderType } from 'utils/groups'
import {
  addGroupModelToServer,
  useCalendarSync
} from 'components/providers/CalendarSyncProdivder'
import { useTour } from '@reactour/tour'
import { set } from 'cypress/types/lodash'
import ColorsPopup from '../ColorsPopup'
import { isTrulyEmpty } from 'utils/object'
import { RemoveThirdPartyEvents } from 'services/db/Queries/Event'
import { syncLocalCalendarsToServer } from 'utils/api/sync_local_calendars_to_server/sync_local_calendars_to_server'
import { TierFeatures } from 'utils/api/group_state'
import { GROUP_INFO_ACTION_TYPES } from 'types/api/group'
import { getOrUpdateDeviceId } from 'utils/device'
import Database from 'services/db/Database'
import dayjs from 'dayjs'
import { isGroupcalApp } from 'utils/appType'

export interface ILoading {
  setLoading: (b: boolean) => void
}

interface ContentProps {
  group: Model<GroupDoc>
  userSettings: Model<UserSettingsDoc>
  setGroupOpen: (b: boolean) => void
  account: Model<AccountDoc>
}

function GroupPopupContent(props: ContentProps) {
  const calendarSync = useCalendarSync()
  const calendar = useCalendar()
  const [showEmbedDialog, onShowEmbedDialog] = useState(false)
  const hideEmbedDialog = () => {
    onShowEmbedDialog(false)

    LocalStorage.remove(LocalStorageKey.VISIBLE_EVENT)
    props.setGroupOpen(false)
  }
  const handleShowEmbedDialog = () => {
    onShowEmbedDialog(true)
  }
  const { error, setError, setLoading, loading } = useProgress()
  const connection = useConnection()
  const [editName, onEditName] = useState(false)
  const [showOutOfTierDialog, onShowOutOfTierDialog] = useState(false)

  const isUserLeftGroup =
    isUserActiveInGroup(
      props.group,
      LocalStorage.get(LocalStorageKey.USER_ID) ?? ''
    ) === false

  const participantsToShow: Record<string, GroupParticipant> = {}

  const activeTier = highestActiveTier(props.account)
  const isOutOfTier = isGroupOutOfTier(activeTier, props.group, props.account)

  for (
    let index = 0;
    index < Object.keys(props.group.Participants ?? {}).length;
    index++
  ) {
    const key = Object.keys(props.group.Participants)[index]
    const participant = props.group.Participants[key]

    if (participant.Status === GroupParticipantStatus.ACTIVE)
      participantsToShow[key] = participant
  }

  const onPicSelected = async (pic: string) => {
    if (connection.isConnectionAvailable()) {
      setLoading(true)
      try {
        await updateGroupPhoto(pic, props.group)
      } catch (e) {
        Logger.red(String(e))
      }
      setLoading(false)
    }
  }

  const onChangeGroupName = async (title: string) => {
    if (connection.isConnectionAvailable()) {
      if (title != props.group.Name) {
        setLoading(true)
        try {
          await updateGroupTitle(title, props.group)
        } catch (e) {
          setLoading(false)
          Logger.red(String(e))
          setError(parseErrorToMessage(e))
          return false
        }
        setLoading(false)
      }
    }

    onEditName(false)
    return true
  }

  const hideGroupPopup = () => {
    props.setGroupOpen(false)
    calendar.onGroupInfo(undefined)
  }

  const hideErrorDialog = () => {
    setError('')
  }

  const isCurrentGroupThirdParty = isConvertedGroup(props.group)

  const canEditNameOrPhoto =
    !isOutOfTier &&
    (props.group.GroupSettings?.IsAllParticipantsCanEditGroupMetadata === '1' ||
      isUserAdminInGroup(
        props.group,
        LocalStorage.get(LocalStorageKey.USER_ID) ?? ''
      ) ||
      isUserOwnerOfGroup(
        props.group,
        LocalStorage.get(LocalStorageKey.USER_ID) ?? ''
      )) &&
    isUserActiveInGroup(
      props.group,
      LocalStorage.get(LocalStorageKey.USER_ID) ?? ''
    ) &&
    props.group.VerifiedGroupStatus != GroupVerified.VERIFIED &&
    !isCurrentGroupThirdParty

  const removeGoogleCalendar = () => {
    calendarSync
      .logout(props.group)
      .then(() => {
        props.setGroupOpen(false)
        calendar.onGroupServer('')
      })
      .finally(() => {
        if (isGroupDefault(props.userSettings, props.group._id)) {
          performUpdateGroupSettings(props.userSettings, props.group._id, {
            Default: '0',
            GroupStatus: GroupStatus.ARCHIVED
          })
        }

        resetConvertedGroupState(props.group)
        if (props.group.PersonalGroupSyncStatus === '2') {
          updateGroupPersonalSyncStatus('', props.group)
        }
        if (
          !isTrulyEmpty(props.group.PrivateLinkPassword) &&
          !isTrulyEmpty(props.group.PrivateLinkUrl)
        ) {
          updateLinkOnServer(
            {
              PrivateLinkUrl: '',
              PrivateLinkPassword: '',
              ActionType: GROUP_INFO_ACTION_TYPES.METADATA,
              UserID: LocalStorage.get(LocalStorageKey.USER_ID) ?? '',
              GroupID: props.group._id,
              DeviceChangeID: getOrUpdateDeviceId()
            },
            props.group
          )
        }
      })
  }
  function unlinkTitle(group: Model<GroupDoc>): string | undefined {
    const provider = groupToProvider(group)

    if (provider === ProviderType.GOOGLE) {
      return `${t('removeGoogleCalendar')}`
    } else if (provider === ProviderType.OUTLOOK) {
      return `${t('removeOutlookCalendar')}`
    } else if (provider === ProviderType.APPLE) {
      return `${t('removeAppleCalendar')}`
    }

    return ''
  }

  function dialogTitle(group: Model<GroupDoc>): string | undefined {
    const provider = groupToProvider(group)

    if (provider === ProviderType.GOOGLE) {
      return `${t('areYouSureDeleteGoogle')}`
    } else if (provider === ProviderType.OUTLOOK) {
      return `${t('areYouSureDeleteOutlook')}`
    } else if (provider === ProviderType.APPLE) {
      return `${t('areYouSureDeleteApple')}`
    }

    return ''
  }

  const [showColorPopup, setShowColorPopup] = useState(false)

  return (
    <Box
      sx={{
        overflowY: 'auto',
        height: '600px'
      }}
    >
      <LoadingContainer loading={loading ? 1 : 0} />
      <PopupSection>
        <Styled.GroupPopupAvatarContainer>
          <div style={{ pointerEvents: canEditNameOrPhoto ? 'all' : 'none' }}>
            <ContainerWithBottomBadgeIcon
              showLock={isOutOfTier}
              topRightIcon={
                isGroupChannel(props.group) ? (
                  <GroupChannelIcon width={18} height={18} />
                ) : undefined
              }
              marginRight={-4}
            >
              <CircleImagePicker
                fallback={defaultGroupImage(props.group)}
                currentPicture={props.group.Photo}
                onPicSelected={onPicSelected}
              />
            </ContainerWithBottomBadgeIcon>
          </div>
          <InputWithAction
            disabled={!canEditNameOrPhoto}
            initialText={props.group?.Name ?? ''}
            editState={editName}
            setLoading={setLoading}
            setEditState={onEditName}
            onSubmit={onChangeGroupName}
            nonActiveChild={
              <CalendarHeaderTitle className="groupTitle">
                <GroupTitle group={props.group} iconHeight="20px" />
              </CalendarHeaderTitle>
            }
          />
          {loading ? (
            <></>
          ) : (
            <Styled.GroupPopupCloseButton
              id="groupPopupCloseButton"
              sx={{
                right: i18n.dir() === 'ltr' ? '-6px' : 'calc(100% - 22px)',
                top: -25
              }}
              size="sm"
              onClick={hideGroupPopup}
            >
              <CloseIcon />
            </Styled.GroupPopupCloseButton>
          )}
          {canEditNameOrPhoto && (
            <Box
              sx={{
                position: 'absolute',
                right: i18n.dir() === 'ltr' ? '0' : 'calc(100% - 16px)',
                top: 12
              }}
            >
              <ColorsPopup
                open={showColorPopup}
                setOpen={setShowColorPopup}
                color={props.group.GroupColor}
                setColor={(color) => {
                  if (connection.isConnectionAvailable()) {
                    setLoading(true)
                    updateGroupColor(color, props.group)
                      .then(() => {
                        // do nothing
                        setLoading(false)
                      })
                      .catch((error) => {
                        setLoading(false)
                        Logger.red(String(error))
                        setError(parseErrorToMessage(error))
                      })
                  }
                }}
              />
            </Box>
          )}
        </Styled.GroupPopupAvatarContainer>
      </PopupSection>
      {!isCurrentGroupThirdParty &&
      (isOwnerForGroup(props.group) ||
        isUserAdminInGroup(
          props.group,
          LocalStorage.get(LocalStorageKey.USER_ID) ?? ''
        ) ||
        (props.group?.BusinessInfo?.Text.length ?? 0) > 0) ? (
        <PopupSection
          sectionId="aboutSection"
          withProBadge={
            isOwnerForGroup(props.group) &&
            (isOutOfTier ||
              activeTier <
                featureToMinimumTierNeeded(TierFeatures.BUSINESSINFO))
          }
          title={`${t('aboutCalendar', { calendarName: props.group.Name })}`}
        >
          <BusinessInfoSection
            group={props.group}
            setLoading={setLoading}
            account={props.account}
          />
        </PopupSection>
      ) : (
        <></>
      )}

      {isUserLeftGroup ? (
        <Typography color={'red'} align={'center'} padding={2}>
          {isUserLeft(
            props.group,
            LocalStorage.get(LocalStorageKey.USER_ID) ?? ''
          )
            ? t('youLeft')
            : t('youRemovedFromCalendar')}
        </Typography>
      ) : (
        <MemberCanSection
          error={error}
          account={props.account}
          setError={setError}
          group={props.group}
          setLoading={setLoading}
        />
      )}
      <SettingsForCalendarSection
        account={props.account}
        group={props.group}
        userSettings={props.userSettings}
        setLoading={setLoading}
      />
      {isGroupcalApp() && <MembersSection {...props} />}
      {isCurrentGroupThirdParty ? (
        <></>
      ) : (
        <OnGroupActionSection
          {...props}
          activeTier={activeTier}
          handleShowEmbedDialog={handleShowEmbedDialog}
        />
      )}
      {isCurrentGroupThirdParty &&
      props.group.OwnerID === LocalStorage.get(LocalStorageKey.USER_ID) ? (
        <PopupSection>
          {props.group.PersonalGroupSyncStatus !== '' ? (
            <PopupSectionRow
              onlyTitle
              title={t('stopSync')}
              action={() => {
                resetConvertedGroupState(props.group)
              }}
            />
          ) : (
            <></>
          )}
          <ExitGroupButton
            group={props.group}
            setLoading={setLoading}
            rowTitle={unlinkTitle(props.group)}
            dialogTitle={dialogTitle(props.group)}
            positiveTitle={`${t('unlink')}`}
            action={removeGoogleCalendar}
          />
        </PopupSection>
      ) : isUserLeftGroup ? (
        <></>
      ) : !isOwnerForGroup(props.group) ? (
        <PopupSection>
          <ExitGroupButton group={props.group} setLoading={setLoading} />
        </PopupSection>
      ) : (
        <></>
      )}
      {error && (
        <ConfirmationDialog
          title={t('cantDoThat')}
          content={error}
          rightButtonHidden
          leftButton={`${t('ok')}`}
          leftButtonColor={APP_COLOR}
          handleLeftButton={hideErrorDialog}
          open={error.length > 0}
          handleClose={hideErrorDialog}
        />
      )}
      {showEmbedDialog && (
        <CalendarProvider>
          <EmbedCalendarDialog
            account={props.account}
            group={props.group}
            userSettings={props.userSettings}
            open={showEmbedDialog}
            handleClose={hideEmbedDialog}
          />
        </CalendarProvider>
      )}
      <TierHover
        isOutOfTier={isOutOfTier}
        topOffset={75}
        account={props.account}
        group={props.group}
        opened={showOutOfTierDialog}
        setOpen={onShowOutOfTierDialog}
      />
    </Box>
  )
}

interface OnGroupActionsProps extends ContentProps {
  activeTier: Tier
  handleShowEmbedDialog?: () => void
}

function OnGroupActionSection(props: OnGroupActionsProps) {
  const { setError, setLoading } = useProgress()

  if (
    !isUserAdminInGroup(
      props.group,
      LocalStorage.get(LocalStorageKey.USER_ID) ?? ''
    )
  ) {
    return <></>
  }
  return (
    <PopupSection>
      {isUserAdminInGroup(
        props.group,
        LocalStorage.get(LocalStorageKey.USER_ID) ?? ''
      ) ? (
        <div>
          {props.group.PrivacyMode === GroupPrivacy.CHANNEL ? (
            <></>
          ) : (
            <ChannelToggle group={props.group} setLoading={setLoading} />
          )}
          <MakeGroupVerified
            currentTier={props.activeTier}
            group={props.group}
          />
        </div>
      ) : (
        <></>
      )}
      {props.group.PrivateLinkUrl && (
        <PopupSectionRow
          onlyTitle
          title={t('embedCalendar')}
          action={props.handleShowEmbedDialog}
        />
      )}
    </PopupSection>
  )
}

function MembersSection(props: ContentProps) {
  const { setError, setLoading } = useProgress()

  const isPersonalCalendar =
    isConvertedGroup(props.group) &&
    groupToProvider(props.group) !== ProviderType.GOOGLE

  const shouldShowParticipants =
    props.group.PrivacyMode === GroupPrivacy.PRIVATE ||
    (props.group.PrivacyMode === GroupPrivacy.CHANNEL &&
      (isUserAdminInGroup(
        props.group,
        LocalStorage.get(LocalStorageKey.USER_ID) ?? ''
      ) ||
        props.group.OwnerID === LocalStorage.get(LocalStorageKey.USER_ID))) ||
    isTrulyEmpty(props.group.CalendarEmail) === false

  const isUserActive = isUserActiveInGroup(
    props.group,
    LocalStorage.get(LocalStorageKey.USER_ID) ?? ''
  )

  const provider = groupToProvider(props.group)

  if (provider === ProviderType.OUTLOOK) {
    return <></>
  }

  return (
    <PopupSection
      sectionId="membersSection"
      title={
        isPersonalCalendar
          ? undefined
          : `${t('membersInGroup', {
              memberCount: Object.keys(
                getActiveParticipants(props.group.Participants)
              ).length
            })}`
      }
      padding={theme.spacing(isPersonalCalendar ? 2 : 0, 2, 2, 0)}
    >
      {shouldShowParticipants ? (
        <>
          {isAddingMemberAllowedForGroup(props.group) &&
          (isUserActive ||
            isUserAdminInGroup(
              props.group,
              LocalStorage.get(LocalStorageKey.USER_ID) ?? ''
            )) ? (
            props.group.PrivateLinkUrl ? (
              <LinkActions group={props.group} />
            ) : isUserAdminInGroup(
                props.group,
                LocalStorage.get(LocalStorageKey.USER_ID) ?? ''
              ) ? (
              <AddLinkContainer {...props} />
            ) : (
              <></>
            )
          ) : (
            <></>
          )}
          {props.group.Participants != null && shouldShowParticipants ? (
            <MembersList
              group={props.group}
              groupId={props.group._id}
              setLoading={setLoading}
              participants={props.group.Participants}
              withoutActions={false}
              localParticipants={[]}
            />
          ) : (
            <></>
          )}
        </>
      ) : props.group.PrivateLinkUrl &&
        props.group.PrivacyMode === GroupPrivacy.CHANNEL ? (
        <InviteLinkRow link={props.group.PrivateLinkUrl} />
      ) : (
        <></>
      )}
    </PopupSection>
  )
}

function AddLinkContainer(props: ContentProps) {
  const connection = useConnection()
  const { setError, setLoading } = useProgress()

  const [showGroupSharingExplanation, setShowGroupSharingExplanation] =
    useState(false)

  const updateGroupWithLink = async () => {
    if (connection.isConnectionAvailable()) {
      setLoading(true)

      if (isConvertedGroup(props.group)) {
        // create a group here
        setShowGroupSharingExplanation(true)
      } else {
        try {
          const updated = await generateLinkForGroup(props.group)

          try {
            await navigator.clipboard.writeText(updated.PrivateLinkUrl)
          } catch (e) {}
        } catch (e) {
          setError(parseErrorToMessage(e))
        }
      }

      setLoading(false)
    }
  }

  const calendarSync = useCalendarSync()
  const calendar = useCalendar()

  const handlePersonalCalenadrShare = async () => {
    setShowGroupSharingExplanation(false)
    setLoading(true)

    try {
      if (isTrulyEmpty(props.group._rev)) {
        await RemoveThirdPartyEvents(props.group._id)
          .then(() => {
            return addGroupModelToServer(props.group).then((group) => {
              calendar.onGroupServer(group._id ?? '')
            })
          })
          .then(() => {
            const provider = groupToProvider(props.group)
            if (provider) {
              return calendarSync.sync(provider).then(() => {
                return syncLocalCalendarsToServer(true)
              })
            }
          })
          .then(() => {
            return updateGroupPersonalSyncStatus('2', props.group)
          })
          .then(() => {
            Database.write(async () => {
              props.group.update((local) => {
                local._raw['_sortOrder'] = dayjs().unix()
                local.LastUpdateMessage = {
                  type: t('youSharedThisCalendar')
                }
              })
            })
          })
      } else {
        await generateLinkForGroup(props.group).then(() => {
          return RemoveThirdPartyEvents(props.group._id).then(() => {
            const provider = groupToProvider(props.group)
            if (provider) {
              calendarSync
                .sync(provider)
                .then(() => {
                  return syncLocalCalendarsToServer(true)
                })
                .then(() => {
                  if (props.group.PersonalGroupSyncStatus !== '2')
                    return updateGroupPersonalSyncStatus('2', props.group)
                  else return Promise.resolve()
                })
                .then(() => {
                  Database.write(async () => {
                    props.group.update((local) => {
                      local._raw['_sortOrder'] = dayjs().unix()
                      local.LastUpdateMessage = {
                        type: t('youSharedThisCalendar')
                      }
                    })
                  })
                })
            }
          })
        })
      }
    } catch (e) {
      console.error(e)
      setError(parseErrorToMessage(e))
    }

    setLoading(false)
  }
  return (
    <>
      <PopupSectionRow
        onlyTitle
        account={props.account}
        title={
          <TextButtonContainer>
            <PlusIcon
              style={{
                width: 12,
                height: 12,
                color: theme.palette.button.text.link
              }}
            />
            <Styled.TextButton style={{ marginLeft: 4 }}>
              {t('shareCalendarWithLink')}
            </Styled.TextButton>
          </TextButtonContainer>
        }
        tierFeature={TierFeatures.SHARE_PERSONAL_CALENDAR}
        neededTier={
          !isTrulyEmpty(props.group.CalendarEmail) ? Tier.PRO : Tier.FREE
        }
        action={() => {
          updateGroupWithLink()
        }}
      />
      <ConfirmationDialog
        title={`${t('sharePersonalCalendarTitle')}`}
        content={`${t('sharePersonalCalendarDesc')}`}
        rightButton={`${t('shareLabel')}`}
        leftButton={`${t('cancel')}`}
        handleRightButton={() => {
          handlePersonalCalenadrShare()
        }}
        handleLeftButton={() => {
          setShowGroupSharingExplanation(false)
        }}
        handleClose={() => {
          setShowGroupSharingExplanation(false)
        }}
        open={showGroupSharingExplanation}
      />
    </>
  )
}
interface LinkActionsProps {
  group: Model<GroupDoc>
}

function LinkActions(props: LinkActionsProps) {
  return (
    <>
      <InviteLinkRow link={props.group.PrivateLinkUrl} />
      {isUserAdminInGroup(
        props.group,
        LocalStorage.get(LocalStorageKey.USER_ID) ?? ''
      ) ||
      isUserOwnerOfGroup(
        props.group,
        LocalStorage.get(LocalStorageKey.USER_ID) ?? ''
      ) ? (
        <RemoveLinkRow group={props.group} />
      ) : (
        <></>
      )}
    </>
  )
}

interface GroupPopupProps {
  setOpen: (b: boolean) => void
  isOpen: boolean
  children: ReactNode
  group: Model<GroupDoc>
  groupID: string
  userSettings: Model<UserSettingsDoc>
  account: Model<AccountDoc>
}

function GroupPopup(props: GroupPopupProps) {
  const calendar = useCalendar()
  const { currentStep, setCurrentStep, setIsOpen } = useTour()
  useEffect(() => {
    if (props.isOpen)
      LocalStorage.set(LocalStorageKey.SHOWING_GROUP_INFO, props.groupID)
    else LocalStorage.remove(LocalStorageKey.SHOWING_GROUP_INFO)
  }, [props.isOpen])

  return (
    <ProgressProvider>
      <Popup
        fallbackPlacements={['right', 'bottom']}
        placement="right"
        open={props.isOpen}
        setOpen={props.setOpen}
        rightClickClose
        onClickAwayAction={() => {
          if (LocalStorage.get(LocalStorageKey.SHOULD_KEEP_POPUP) === '1')
            return

          calendar.onGroupInfo(undefined)
          props.setOpen(false)
        }}
        onHide={() => {
          if (LocalStorage.get(LocalStorageKey.SHOULD_KEEP_POPUP) === '1')
            return

          props.setOpen(false)

          if (
            (LocalStorage.get(LocalStorageKey.USER_FINSHED_ONBOARDING) ??
              '0') === '0'
          ) {
            setCurrentStep(currentStep + 1)
            setIsOpen(true)
          }
        }}
        closeOnTouchOutside={false}
        content={
          <ClickAwayListener
            onClickAway={() => {
              if (LocalStorage.get(LocalStorageKey.SHOULD_KEEP_POPUP) === '1')
                return

              calendar.onGroupInfo(undefined)
              props.setOpen(false)
            }}
          >
            <div>
              <GroupPopupContent setGroupOpen={props.setOpen} {...props} />
            </div>
          </ClickAwayListener>
        }
      >
        {props.children}
      </Popup>
    </ProgressProvider>
  )
}
export default compose(
  withObservables([], () => ({
    account: ObserveService.Account()
  })),
  withObservables([], () => ({
    userSettings: ObserveService.UserSettings()
  })),
  withObservables(['groupID', 'userSettings'], (props: GroupPopupProps) => ({
    group: ObserveService.GroupByServerID(props.groupID)
  }))
)(GroupPopup as ComponentType<unknown>) as any
